import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MyClassesService } from '@core/services/my-classes.service';
import { AddEditComponent } from '@shared/components/drdp-tables/add-edit/add-edit.component';
import { AuthService } from '@core/services/auth.service';
import { PermissionService } from '@core/services/permission.service';
import { MatTableDataSource } from '@angular/material/table';
import {
  IUserClassesDto,
  IUserClassSearchRequestDto,
} from '@core/interfaces/iclasses';
import { MatPaginator } from '@angular/material/paginator';
import { Subscription, of, switchMap, take } from 'rxjs';
import { SelectType } from '@core/enums/select';
import { IAgency } from '@core/interfaces/iagency';
import { ISites } from '@core/interfaces/isites';
import { IRatingPeriod } from '@core/interfaces/iratingperiod';
import { ReEnrollClassroomComponent } from './re-enroll-classroom/re-enroll-classroom.component';
import { MatDialog } from '@angular/material/dialog';
import { setIconDisplayObject } from '@core/services/helper.service';
import { IState } from '@core/interfaces/istate';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SelectStateComponent } from '@shared/components/dropdowns/select-state/select-state.component';
import { SelectAgencyComponent } from '@shared/components/dropdowns/select-agency/select-agency.component';
import { Permission } from '@core/enums/permissions';
import { ToastService } from '@core/services/toast.service';
import { formatStringToStringArr } from '@core/services/helper.service';
import { StudentClassEnrollmentService } from '@core/services/student-class-enrollment.service';
import { RatingService } from '@core/services/rating.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-my-classes',
    templateUrl: './my-classes.component.html',
    styleUrls: ['./my-classes.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MyClassesComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(AddEditComponent) table?: AddEditComponent;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('stateSelect') stateSelect!: SelectStateComponent;
  @ViewChild('agencySelect') agencySelect!: SelectAgencyComponent;

  private subscriptions = new Subscription();
  classroomsForm: FormGroup | any;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  totalData = 0;
  pageSizeOptions: number[] = [10, 25, 50, 100];
  pageIndex: number = 0;
  pageSize: number = 10;
  tableData?: any;
  payload: IUserClassSearchRequestDto = {};
  childMovementErrorMessage: string = '';
  accessToAllSites = this.PermissionService.checkPermission(
    Permission.AccessAllSites
  );
  accessToAllAgencies = this.PermissionService.checkPermission(
    Permission.AccessAllAgencies
  );
  accessToClassroomSearch = this.PermissionService.checkPermission(
    Permission.ClassroomSearch
  );
  accessToClassReenroll = this.PermissionService.checkPermission(
    Permission.ClassReenroll
  );

  tableColumns: any[] = [];
  user = this.authService.getCurrentUser();
  receivedPayload?: any;
  clearDropdowns = false;

  get stateId() {
    return this.classroomsForm.get('stateId');
  }
  get agencyId() {
    return this.classroomsForm.get('agencyId');
  }
  get siteId() {
    return this.classroomsForm.get('siteId');
  }
  get ratingPeriodId() {
    return this.classroomsForm.get('ratingPeriodId');
  }
  get classroomId() {
    return this.classroomsForm.get('classroomId');
  }
  get selectType() {
    return SelectType;
  }

  constructor(
    private fb: FormBuilder,
    private myClasses: MyClassesService,
    private route: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    public PermissionService: PermissionService,
    private toastService: ToastService,
    public modal: MatDialog,
    private enrollmentService: StudentClassEnrollmentService,
    private ratingService: RatingService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.childMovementErrorMessage = this.translocoService.translate(
      'user.manageEnrollment.myClasses.noStudentsToReEnroll'
    );
    this.buildTableColumns();
    this.initializeForm();

    this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
      const hasParams = Object.keys(params || {}).length > 0;
  
      if (hasParams) {
        if (params['stateId']) this.stateId.setValue(+params['stateId']);
        if (params['agencyId']) this.agencyId.setValue(+params['agencyId']);
        if (params['siteId']) this.siteId.setValue(+params['siteId']);
        if (params['ratingPeriodId']) this.ratingPeriodId.setValue(+params['ratingPeriodId']);
        if (params['classroomId']) this.classroomId.setValue(+params['classroomId']);
  
        if (params['itemsPerPage']) this.pageSize = +params['itemsPerPage'];
        if (params['pageIndex']) this.pageIndex = +params['pageIndex'];
  
        this.payload = {
          ...this.classroomsForm.value,
          itemsPerPage: this.pageSize,
          pageIndex: this.pageIndex,
          userId: this.user.userId,
        };
      }

      this.loadClasses();
    });
  }

  buildTableColumns(): void {
    this.tableColumns = [
      {
        columnDef: 'viewStudents',
        header: this.translocoService.translate(
          'user.manageEnrollment.myClasses.columns.viewStudents'
        ),
        icon: 'file-alt',
        type: 'icon',
        colorClass: 'text-drdpblue-300',
        cursorClass: 'cursor-pointer',
      },
      {
        columnDef: 'name',
        header: this.translocoService.translate(
          'user.manageEnrollment.myClasses.columns.classroom'
        ),
        type: 'text',
        isSortable: true,
      },
      {
        columnDef: 'siteName',
        header: this.translocoService.translate(
          'user.manageEnrollment.myClasses.columns.site'
        ),
        type: 'text',
        isSortable: true,
      },
      {
        columnDef: 'agencyName',
        header: this.translocoService.translate(
          'user.manageEnrollment.myClasses.columns.agency'
        ),
        type: 'text',
        isSortable: true,
      },
      {
        columnDef: 'teachersNames',
        header: this.translocoService.translate(
          'user.manageEnrollment.myClasses.columns.teacher'
        ),
        type: 'bulletList',
        isSortable: true,
      },
      {
        columnDef: 'teacherOfRecordName',
        header: this.translocoService.translate(
          'user.manageEnrollment.myClasses.columns.teacherOfRecord'
        ),
        type: 'text',
        isSortable: true,
      },
      {
        columnDef: 'ratingPeriodName',
        header: this.translocoService.translate(
          'user.manageEnrollment.myClasses.columns.ratingPeriod'
        ),
        type: 'text',
        isSortable: true,
      },
      {
        columnDef: 'studentCount',
        header: this.translocoService.translate(
          'user.manageEnrollment.myClasses.columns.childrenCount'
        ),
        type: 'text',
        isSortable: true,
      },
      {
        columnDef: 'lockedStudents',
        header: this.translocoService.translate(
          'user.manageEnrollment.myClasses.columns.locked'
        ),
        type: 'text',
        isSortable: true,
      },
      this.accessToClassReenroll ?
      {
        columnDef: 'reEnroll',
        header: this.translocoService.translate(
          'user.manageEnrollment.myClasses.columns.reEnroll'
        ),
        icon: 'file-medical text-drdpblue-300',
        type: 'icon',
        colorClass: 'text-drdpblue-300',
        cursorClass: 'cursor-pointer',
        tooltip: this.translocoService.translate(
          'user.manageEnrollment.myClasses.reEnrollTooltip'
        ),
      } : null
    ].filter(Boolean);
  }

  loadClasses(resetPage?: boolean) {
    if (!this.accessToClassroomSearch) {
      this.getMyClasses();
    } else {
      this.search(resetPage);
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.paginator._intl.itemsPerPageLabel = '';
  }

  getMyClasses(): void {
    this.payload.stateId = this.user.stateId;
    this.payload.agencyId = this.user.agencyId;
    this.payload.userId = this.user.userId;

    if (this.paginator) {
      this.payload.pageIndex = this.paginator.pageIndex + 1;
      this.payload.itemsPerPage = this.paginator.pageSize;
    } else {
      this.payload.pageIndex = this.pageIndex;
      this.payload.itemsPerPage = this.pageSize;
    }
    this.myClasses
      .getUserClasses(this.payload)
      .subscribe((res: IUserClassesDto) => {
        if (res) {
          this.initializeTableData(res);
        }
      });
  }

  initializeForm(): void {
    this.classroomsForm = this.fb.group({
      stateId: [this.user.stateId, Validators.required],
      agencyId: [this.user.agencyId],
      siteId: [null],
      ratingPeriodId: [null],
      classroomId: [null],
    });

    if (!this.accessToAllAgencies) {
      this.agencyId.setValidators([Validators.required]);
      this.agencyId.updateValueAndValidity();
    }
  }

  initializeTableData(res: any): void {
    res.classes = res.classes.items.map((data: any) => ({
      ...data,
      displayIcons: setIconDisplayObject(this.tableColumns, data),
      teachersNames: formatStringToStringArr(data.teachersNames),
    }));
    this.dataSource = new MatTableDataSource(res.classes);
    this.totalData = res.totalData;
    this.tableData = res.classes;
  }

  paginateTable(resetPage?: boolean): void {
    if (this.classroomsForm.invalid) {
      this.toastService.error(
        this.translocoService.translate(
          'user.manageEnrollment.myClasses.requiredFieldsError'
        )
      );
      return;
    }
    this.payload = this.classroomsForm.value;
    this.payload.userId = this.user.userId;
    if (this.paginator) {
      this.payload.pageIndex = this.paginator.pageIndex + 1;
      this.payload.itemsPerPage = this.paginator.pageSize;
    } else {
      this.payload.pageIndex = this.pageIndex;
      this.payload.itemsPerPage = this.pageSize;
    }

    if (Object.keys(this.payload).length == 2) {
      this.dataSource = new MatTableDataSource();
      this.totalData = 0;
      this.tableData = [];
    } else {
      this.myClasses
        .getUserClasses(this.payload)
        .subscribe((res: IUserClassesDto) => {
          if (res) {
            if (resetPage) this.paginator.firstPage();
            this.initializeTableData(res);
          }
        });
    }
  }

  onIconClick(event: any): void {
    switch (event.type) {
      case 'viewStudents':
        const qp = {
          stateId: this.stateId?.value ?? null,
          agencyId: this.agencyId?.value ?? null,
          siteId: this.siteId?.value ?? null,
          ratingPeriodId: this.ratingPeriodId?.value ?? null,
          classroomId: this.classroomId?.value ?? null,
          pageIndex: this.paginator ? this.paginator.pageIndex + 1 : this.pageIndex,
          itemsPerPage: this.paginator ? this.paginator.pageSize : this.pageSize,
        };
        this.route.navigate([`/view-students/${event.data.id}`], { queryParams: qp });
        break;
      case 'reEnroll':
        this.openReEnrollModal(event);
        break;
      default:
        break;
    }
  }

  handleState(state: IState) {
    if (!state) {
      this.onClear();
      return;
    }

    this.stateId.setValue(state.id);
    this.payload.stateId = this.stateId.value;
  }

  handleAgency(agency: IAgency) {
    if (!agency) {
      this.payload = {};
      this.agencyId.setValue(null);
      this.ratingPeriodId.setValue(null);
      this.classroomId.setValue(null);
      return;
    }

    const previousAgencyId = this.agencyId.value;
    this.agencyId.setValue(agency.id);

    if (previousAgencyId !== agency.id) {
      this.ratingPeriodId.setValue(null);
      this.classroomId.setValue(null);
      this.payload.ratingPeriodId = null;
      this.payload.classroomId = null;
    }

    this.payload.agencyId = this.agencyId.value;
  }

  handleSites(site: ISites | ISites[] | null): void {
    if (!site || (Array.isArray(site) && site.length === 0)) {
      this.siteId.setValue(null);
      this.payload.siteId = null;
      this.payload.ratingPeriodId = null;
      this.payload.classroomId = null;
      return;
    }

    const selectedSite = Array.isArray(site) ? site[0] : site;
    this.siteId.setValue(selectedSite.id);
    this.payload.siteId = this.siteId.value;
  }

  handleRatingPeriod(ratingPeriod: IRatingPeriod): void {
    if (!ratingPeriod) {
      this.payload.ratingPeriodId = null;
      this.payload.classroomId = null;
      this.ratingPeriodId.setValue(null);
      this.classroomId.setValue(null);
      return;
    }

    this.ratingPeriodId.setValue(ratingPeriod.id);
    this.payload.ratingPeriodId = this.ratingPeriodId.value;
  }

  handleClassroom(classroom: any): void {
    if (!classroom) {
      this.classroomId.setValue(null);
      this.payload.classroomId = null;
      return;
    }

    const selectedClassroom = Array.isArray(classroom)
      ? classroom[0]
      : classroom;
    const id = typeof selectedClassroom === 'number'
      ? selectedClassroom
      : selectedClassroom?.id ?? selectedClassroom?.classroomId ?? null;
    this.classroomId.setValue(id);
    this.payload.classroomId = this.classroomId.value;
  }

  search(resetPage?: boolean): void {
    if (this.classroomsForm.invalid) {
      this.toastService.error(
        this.translocoService.translate(
          'user.manageEnrollment.myClasses.requiredFieldsError'
        )
      );
      return;
    }
    this.paginateTable(resetPage);
  }

  onClear(): void {
    this.dataSource = new MatTableDataSource<any>();
    this.clearDropdowns = true;
    this.totalData = 0;
    this.payload.itemsPerPage = 10;
    this.paginator.pageSize = 10;

    if (!this.stateSelect?.disabled) {
      this.stateId.setValue(null);
      this.payload.stateId = this.stateId.value;
    }

    if (!this.agencySelect?.disabled) {
      this.agencyId.setValue(null);
      this.payload.agencyId = this.agencyId.value;
    }

    this.classroomsForm.patchValue({
      siteId: null,
      ratingPeriodId: null,
      classroomId: null,
    });

    // allow dropdowns to clear once, then resume normal behavior
    setTimeout(() => (this.clearDropdowns = false));
  }

  openReEnrollModal(event: any): void {
    if (event.data.activeStudentCount == 0) {
      this.toastService.error(this.childMovementErrorMessage);
      return;
    }

    this.ratingService.assessmentConfirmationModal().pipe(
      take(1),
      switchMap((confirmed: boolean) => {
        if (!confirmed) return of(false);
        return this.enrollmentService.areEnrollmentsActive({ classId: event.data.id}).pipe(
          switchMap((isActive: boolean) => {
            if (isActive) {
              const modalRef = this.modal.open(ReEnrollClassroomComponent, {
                data: event ? { ...event.data, agencyId: this.agencyId.value } : undefined,
              });
              return modalRef.afterClosed();
            } else {
              this.toastService.error(this.childMovementErrorMessage);
              return of(false);
            }
          })
        );
      })
    ).subscribe((success) => {
      if (success) {
        this.loadClasses();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
